import { auth as dbAuth } from "@/db";
import { signInWithCustomToken, signOut } from "firebase/auth";
import AuthService from "../services/auth.service";

// Initialize state with Firebase auth status
const user = JSON.parse(localStorage.getItem("user"));
const initialState = user
  ? {
      status: {
        loggedIn: true,
        firebaseAuthenticated: false,
      },
      user,
      selected_user: null,
    }
  : {
      status: {
        loggedIn: false,
        firebaseAuthenticated: false,
      },
      user: null,
      selected_user: null,
    };

const snackbar = {
  show: false,
  message: "",
  color: "",
};

export const auth = {
  namespaced: true,
  state: { ...initialState, snackbar },
  actions: {
    clearSnack({ commit }) {
      commit("clearSnack");
    },

    setSelectedUser({ commit }, item) {
      commit("setSelectedUser", item);
    },

    async login({ commit, dispatch }, credentials) {
      try {
        const response = await AuthService.login(credentials);

        if (response.status === 200) {
          // Commit Laravel authentication
          commit("loginSuccess", response.data.data);

          // Authenticate with Firebase
          await dispatch("firebaseAuth", response.data.data.firebase_token);

          return Promise.resolve(response.data.data);
        }

        commit("loginFailure", response);
        return Promise.reject(response);
      } catch (error) {
        // Ensure full cleanup on any error
        await dispatch("logout");
        commit("loginFailure", error);
        return Promise.reject(error);
      }
    },

    // inside actions: { … }
    async verifyUser({ commit, dispatch }, credentials) {
      try {
        const response = await AuthService.verifyUser(credentials);
        if (response.status === 200) {
          // 1. Commit Laravel auth (exactly like login)
          commit("loginSuccess", response.data.data);
          // 2. Firebase auth
          await dispatch("firebaseAuth", response.data.data.firebase_token);
          // 3. Return the same userData
          return Promise.resolve(response.data.data);
        }
        commit("loginFailure", response);
        return Promise.reject(response);
      } catch (error) {
        // on any error, force full logout (just like login failure)
        await dispatch("logout");
        commit("loginFailure", error);
        return Promise.reject(error);
      }
    },

    async firebaseAuth({ commit }, firebaseToken) {
      try {
        await signInWithCustomToken(dbAuth, firebaseToken);
        commit("setFirebaseAuth", true);
        return true;
      } catch (error) {
        console.error("Firebase auth error:", error);
        commit("setFirebaseAuth", false);
        throw new Error("Failed to authenticate with chat service");
      }
    },

    async logout({ commit }) {
      try {
        // Clear Laravel authentication
        AuthService.logout();

        // Clear Firebase authentication
        await signOut(dbAuth);
      } catch (error) {
        console.error("Logout error:", error);
      } finally {
        // Reset all auth state
        commit("logout");
      }
    },

    // oauthLogin({ commit }, user) {
    //   return AuthService.oathLoginCallback(user).then(
    //     (response) => {
    //       if (response.status == 200) {
    //         commit("loginSuccess", response.data.data);
    //         return Promise.resolve(response.data.data);
    //       } else {
    //         commit("loginFailure", response);
    //         return Promise.reject(response);
    //       }
    //     },
    //     (error) => {
    //       commit("loginFailure", error);
    //       return Promise.reject(error);
    //     }
    //   );
    // },

    async oauthLogin({ commit, dispatch }, user) {
      try {
        const response = await AuthService.oathLoginCallback(user);
        if (response.status === 200) {
          // 1) Commit Laravel/Sanctum token
          commit("loginSuccess", response.data.data);
          // 2) Sign into Firebase with the custom token
          await dispatch("firebaseAuth", response.data.data.firebase_token);
          return response.data.data;
        }
        commit("loginFailure", response);
        return Promise.reject(response);
      } catch (error) {
        commit("loginFailure", error);
        return Promise.reject(error);
      }
    },
  },

  mutations: {
    loginSuccess(state, userData) {
      state.status.loggedIn = true;
      state.status.firebaseAuthenticated = false; // Reset until Firebase auth completes
      state.snackbar.show = true;
      state.snackbar.color = "success";
      state.snackbar.message = "Login successful!";

      const user = {
        ...userData,
        // Remove sensitive Firebase data from stored user
        firebase_token: undefined,
      };

      localStorage.setItem("user", JSON.stringify(user));
      localStorage.setItem("token", userData.token);
      state.user = user;
    },

    setFirebaseAuth(state, status) {
      state.status.firebaseAuthenticated = status;
      if (status) {
        state.snackbar.message = "Chat service connected!";
      }
    },

    loginFailure(state, error) {
      console.error("Login error:", error);
      state.status.loggedIn = false;
      state.status.firebaseAuthenticated = false;
      state.user = null;
      state.snackbar.show = true;
      state.snackbar.color = "error";
      state.snackbar.message =
        error?.response?.data?.message ||
        error?.message ||
        "Login failed. Please try again.";
    },

    logout(state) {
      state.status.loggedIn = false;
      state.status.firebaseAuthenticated = false;
      state.user = null;
      state.snackbar.show = false;
      state.snackbar.color = "";
      state.snackbar.message = "";
      localStorage.removeItem("user");
      localStorage.removeItem("token");
    },

    validationError(state, errors) {
      let msg = "";
      const errorsArray = Object.values(errors);
      errorsArray.forEach((element) => {
        msg += element + ".";
      });
      state.snackbar.show = true;
      state.snackbar.color = "error";
      state.snackbar.message = msg;
    },

    clearSnack(state) {
      state.snackbar.show = false;
      state.snackbar.color = "";
      state.snackbar.message = "";
    },

    setSelectedUser(state, item) {
      state.selected_user = item;
    },
  },

  getters: {
    user: (state) => state.user,
    isFullyAuthenticated: (state) =>
      state.status.loggedIn && state.status.firebaseAuthenticated,
  },
};
